import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  BedDouble,
  Users,
  Maximize2,
} from "lucide-react";
import Button from "@/components/ui/Button";
import PriceBreakdown from "./PriceBreakdown";
import { useAvailability } from "../hooks/useAvailability";
import {
  formatCurrency,
  formatArea,
  calculateGst,
} from "@/lib/utils/formatters";
import { usePublicRooms } from "@/features/rooms/hooks/usePublicRooms";
import { MEAL_PLANS } from "@/types/rooms";
import type { MealPlan } from "@/types/rooms";
import type {
  BookingDraft,
  AvailableRoomType,
  PriceBreakdown as PriceBreakdownType,
} from "@/types/booking";
import { cn } from "@/lib/utils/cn";

interface StepRoomsProps {
  draft: BookingDraft;
  onSelect: (
    roomTypeId: string,
    roomTypeName: string,
    mealPlan: MealPlan,
    pricing: PriceBreakdownType,
  ) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function StepRooms({
  draft,
  onSelect,
  onNext,
  onBack,
}: StepRoomsProps) {
  const { available, loading, error, searched, checkAvailability } =
    useAvailability();
  const { data: roomTypes = [] } = usePublicRooms();

  // Per-room selected meal plan state
  const [selectedMealPlans, setSelectedMealPlans] = useState<
    Record<string, MealPlan>
  >({});

  useEffect(() => {
    checkAvailability({
      checkIn: draft.checkIn,
      checkOut: draft.checkOut,
      adults: draft.adults,
      children: draft.children,
    });
  }, []);

  const roomTypeMap = Object.fromEntries(roomTypes.map((rt) => [rt.id, rt]));
  const selectedId = draft.roomTypeId;

  const nights = Math.max(
    0,
    Math.round(
      (new Date(draft.checkOut).getTime() - new Date(draft.checkIn).getTime()) /
        (1000 * 60 * 60 * 24),
    ),
  );

  const handleSelect = (room: AvailableRoomType, mealPlan: MealPlan) => {
    const details = roomTypeMap[room.roomTypeId];
    const pricePerNight =
      details?.mealPlanPricing?.[mealPlan] ?? room.basePrice;
    const subtotal = pricePerNight * nights;
    const { gstRate, gstAmount, total } = calculateGst(subtotal, pricePerNight);

    const pricing: PriceBreakdownType = {
      basePrice: pricePerNight,
      nights,
      subtotal,
      gstRate,
      gstAmount,
      total,
      mealPlan,
    };

    onSelect(room.roomTypeId, room.roomTypeName, mealPlan, pricing);
  };

  const getMealPlanForRoom = (roomTypeId: string): MealPlan =>
    selectedMealPlans[roomTypeId] ?? "EP";

  const handleMealPlanChange = (roomTypeId: string, plan: MealPlan) => {
    setSelectedMealPlans((prev) => ({ ...prev, [roomTypeId]: plan }));
    // If this room is already selected, update pricing live
    if (selectedId === roomTypeId) {
      const room = available.find((r) => r.roomTypeId === roomTypeId);
      if (room) handleSelect(room, plan);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-metro-green-950 mb-1">
          Choose your room
        </h2>
        <p className="text-sm text-metro-ink-subtle">
          Showing availability for{" "}
          <span className="font-medium text-metro-ink">
            {draft.adults} {draft.adults === 1 ? "adult" : "adults"}
            {draft.children > 0 &&
              `, ${draft.children} ${draft.children === 1 ? "child" : "children"}`}
          </span>{" "}
          · {draft.checkIn} → {draft.checkOut}
        </p>
      </div>

      {/* Meal plan legend */}
      <div className="rounded-xl border border-metro-cream-100 bg-metro-cream p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
          Meal Plan Guide
        </p>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          {MEAL_PLANS.map((p) => (
            <div key={p.key} className="text-xs">
              <span className="font-semibold text-metro-green-800">
                {p.key}
              </span>
              <span className="text-metro-ink-muted"> — {p.description}</span>
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex h-48 items-center justify-center gap-3 text-sm text-metro-ink-subtle">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-metro-green-100 border-t-metro-green-800" />
          Checking availability…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {searched && !loading && available.length === 0 && (
        <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 text-center">
          <p className="font-medium text-metro-green-950">
            No rooms available for these dates
          </p>
          <p className="text-sm text-metro-ink-subtle">
            Try different dates or fewer guests.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="mt-2 text-sm font-medium text-metro-green-800 underline-offset-2 hover:underline"
          >
            Change dates
          </button>
        </div>
      )}

      {!loading && available.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
          <div className="space-y-4">
            {available.map((room) => {
              const details = roomTypeMap[room.roomTypeId];
              const isSelected = selectedId === room.roomTypeId;
              const cover = details?.images?.[0];
              const activePlan = getMealPlanForRoom(room.roomTypeId);
              const hasMealPlanPricing = !!details?.mealPlanPricing;

              // Displayed price for active plan
              const displayPrice =
                details?.mealPlanPricing?.[activePlan] ?? room.basePrice;
              const { gstRate: displayGst, total: displayTotal } = calculateGst(
                displayPrice * nights,
                displayPrice,
              );

              return (
                <div
                  key={room.roomTypeId}
                  className={cn(
                    "rounded-xl border-2 bg-white transition",
                    isSelected
                      ? "border-metro-green-800 shadow-card"
                      : "border-gray-100 hover:border-metro-green-200 hover:shadow-card",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(room, activePlan)}
                    className="w-full text-left"
                  >
                    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:gap-4">
                      <div className="h-44 w-full overflow-hidden rounded-lg bg-metro-cream-100 sm:h-28 sm:w-36 sm:shrink-0">
                        {cover ? (
                          <img
                            src={cover}
                            alt={room.roomTypeName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <BedDouble size={24} className="text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-serif text-lg text-metro-green-950">
                              {room.roomTypeName}
                            </h3>
                            <div className="text-right shrink-0">
                              <p className="font-serif text-lg font-medium text-metro-green-950">
                                {formatCurrency(displayTotal)}
                              </p>
                              <p className="text-[11px] text-metro-ink-subtle">
                                incl. {displayGst}% GST · {activePlan}
                              </p>
                            </div>
                          </div>
                          {details && (
                            <p className="mt-1 text-xs text-metro-ink-muted line-clamp-2">
                              {details.shortDescription}
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-metro-ink-subtle">
                          {details && (
                            <>
                              <span className="flex items-center gap-1">
                                <Maximize2 size={12} />
                                {formatArea(details.size)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users size={12} />
                                Up to {details.maxGuests} guests
                              </span>
                            </>
                          )}
                          <span className="text-metro-green-800">
                            {room.availableCount}{" "}
                            {room.availableCount === 1 ? "room" : "rooms"} left
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Meal plan selector */}
                  {hasMealPlanPricing && (
                    <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-metro-ink-subtle">
                        Select Meal Plan
                      </p>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {MEAL_PLANS.map((p) => {
                          const price = details?.mealPlanPricing?.[p.key] ?? 0;
                          const isActive = activePlan === p.key;
                          return (
                            <button
                              key={p.key}
                              type="button"
                              onClick={() =>
                                handleMealPlanChange(room.roomTypeId, p.key)
                              }
                              className={cn(
                                "rounded-lg border px-2.5 py-2 text-left text-xs transition",
                                isActive
                                  ? "border-metro-green-800 bg-metro-green-50"
                                  : "border-gray-200 hover:border-metro-green-300",
                              )}
                            >
                              <p
                                className={cn(
                                  "font-semibold",
                                  isActive
                                    ? "text-metro-green-800"
                                    : "text-metro-ink-muted",
                                )}
                              >
                                {p.key}
                              </p>
                              <p className="text-metro-ink-subtle">
                                {formatCurrency(price)}/night
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {isSelected && (
                    <div className="rounded-b-[10px] bg-metro-green-800 py-1.5 text-center text-xs font-medium text-white">
                      Selected · {activePlan}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden lg:block">
            {selectedId && draft.pricing ? (
              <div className="sticky top-24">
                <PriceBreakdown
                  pricing={draft.pricing}
                  roomTypeName={draft.roomTypeName}
                  checkIn={draft.checkIn}
                  checkOut={draft.checkOut}
                />
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-gray-200 p-6 text-center text-sm text-metro-ink-subtle">
                Select a room to see the price breakdown
              </div>
            )}
          </div>
        </div>
      )}

      {selectedId && draft.pricing && (
        <div className="lg:hidden">
          <PriceBreakdown
            pricing={draft.pricing}
            roomTypeName={draft.roomTypeName}
            checkIn={draft.checkIn}
            checkOut={draft.checkOut}
          />
        </div>
      )}

      <div className="flex justify-between border-t border-gray-100 pt-4">
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={16} />}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          rightIcon={<ArrowRight size={16} />}
          onClick={onNext}
          disabled={!selectedId}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}